
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { UserMenuButton } from './UserMenu/UserMenuButton';
import { UserMenuDropdown } from './UserMenu/UserMenuDropdown';
import { useUserMenuData } from './UserMenu/useUserMenuData';

const UserMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileSubmenuOpen, setProfileSubmenuOpen] = useState(false);
  const navigate = useNavigate();

  const {
    user,
    isInternalUser,
    displayName,
    initials,
    profilePhotoPath,
    role,
    userSlug,
    profileRoute,
    signOut,
    internalLogout
  } = useUserMenuData();

  // Add user stability logging to debug account switching
  useEffect(() => {
    console.log('=== USER MENU USER STATE CHANGE ===');
    console.log('Current user from hook:', user?.id, userSlug);
    console.log('Is internal user:', isInternalUser);
    console.log('=====================================');
  }, [user?.id, userSlug, isInternalUser]);

  const handleSignOut = async () => {
    try {
      if (isInternalUser) {
        console.log('UserMenu: Logging out internal user');
        internalLogout();
      } else {
        console.log('UserMenu: Logging out Supabase user');
        await signOut();
      }
      toast.success('Signed out successfully');
      // Add delay to ensure state is cleared before navigation
      setTimeout(() => {
        navigate('/');
      }, 50);
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to sign out');
    }
  };

  const handleViewProfile = () => {
    console.log('=== VIEW PROFILE DEBUG ===');
    console.log('User:', user);
    console.log('Is Internal User:', isInternalUser);
    console.log('User Slug:', userSlug);
    console.log('Display Name:', displayName);
    console.log('Role:', role);
    console.log('Profile Route:', profileRoute);
    
    // Navigate to the profile route
    navigate(profileRoute);
    setIsOpen(false);
    setProfileSubmenuOpen(false);
  };

  const handleEditProfile = () => {
    console.log('Edit Profile clicked:', { isInternalUser, role, userId: user?.id });
    
    // Always route to the dedicated profile editing page
    navigate('/profile-edit');
    setIsOpen(false);
    setProfileSubmenuOpen(false);
  };

  const handleAccountSettings = () => {
    navigate('/account-settings');
    setIsOpen(false);
    setProfileSubmenuOpen(false);
  };

  const handlePrivacySettings = () => {
    navigate('/privacy-settings');
    setIsOpen(false);
    setProfileSubmenuOpen(false);
  };

  const handleMyTickets = () => {
    navigate('/my-tickets');
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setProfileSubmenuOpen(false);
  };

  if (!user) return null;

  return (
    <div className="relative z-50">
      <div className="relative flex items-center gap-3">
        <UserMenuButton
          onClick={() => setIsOpen(!isOpen)}
          profilePhotoPath={profilePhotoPath}
          initials={initials}
        />
        
        {isOpen && (
          <UserMenuDropdown
            profilePhotoPath={profilePhotoPath}
            initials={initials}
            displayName={displayName}
            role={role}
            profileSubmenuOpen={profileSubmenuOpen}
            onToggleProfileSubmenu={() => setProfileSubmenuOpen(!profileSubmenuOpen)}
            onClose={handleClose}
            onViewProfile={handleViewProfile}
            onEditProfile={handleEditProfile}
            onAccountSettings={handleAccountSettings}
            onPrivacySettings={handlePrivacySettings}
            onMyTickets={handleMyTickets}
            onSignOut={handleSignOut}
          />
        )}
      </div>
    </div>
  );
};

export default UserMenu;
